import * as React from "react";
import * as SlateReact from "slate-react";

import {khanmigoTexKey} from "@repo/component-tex";
import {useMathEditorContext} from "@repo/guide-math-editor-context";
import type {References} from "@repo/slate-contexts";
import {
    referencesKeyDownConfig,
    slateKeyDownConfig,
    type KeyDownConfig,
} from "@repo/slate-helpers";

interface ExtraKeyDownArgs {
    references?: References;
}

interface Args {
    handleSend: () => void;
    enableReferenceKeyHandlers?: boolean;
    extraKeyDownArgs?: ExtraKeyDownArgs;
}

export const useKeyHandlers = ({
    handleSend,
    enableReferenceKeyHandlers,
    extraKeyDownArgs = {},
}: Args): {
    handleKeyDown: React.KeyboardEventHandler<HTMLDivElement>;
    handlePaste: React.ClipboardEventHandler<HTMLDivElement>;
} => {
    const editor = SlateReact.useSlate();
    const {isAnyActive: isAnyMathActive} = useMathEditorContext();

    const handleKeyDown = React.useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.shiftKey || event.metaKey || event.ctrlKey) {
                // allow native behavior for shift, cmd, and ctrl keys so
                //   that overrides do not interfere with hotkeys;
                //   specifically fixes LC-1224 and allows shift+arrow keys
                //   to select text
                return;
            }
            // creating a config to allow prioritizing keydown events
            //   for active features
            let keyDownConfig: KeyDownConfig = {
                ["Enter"]: ({event}) => {
                    event.preventDefault();
                    handleSend();
                },
                ...slateKeyDownConfig,
            };
            if (enableReferenceKeyHandlers) {
                keyDownConfig = {
                    ...keyDownConfig,
                    ...referencesKeyDownConfig,
                };
            }

            keyDownConfig[event.key]?.({event, editor, ...extraKeyDownArgs});
        },
        [extraKeyDownArgs, enableReferenceKeyHandlers, handleSend, editor],
    );

    const handlePaste = React.useCallback(
        (event: React.ClipboardEvent<HTMLDivElement>) => {
            // MathJax adds TeX wrapped in delimiters to the copy data.
            //   We only want to use this wrapped TeX when pasting into the
            //   Slate text area-- not the math editor or other contexts.
            const delimitedTex = event.clipboardData.getData(khanmigoTexKey);
            if (!isAnyMathActive && delimitedTex) {
                event.preventDefault();
                editor.insertText(delimitedTex);
            }
        },
        [isAnyMathActive, editor],
    );
    return {handleKeyDown, handlePaste};
};
